import { useState, useEffect, useCallback } from "react";
import { CreditCard, Plug, CheckCircle2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export function PaymentsView({ user }) {
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [oauthStatus, setOauthStatus] = useState({ square: {}, paypal: {} });
  const [loading, setLoading] = useState(true);
  const [connectingPayment, setConnectingPayment] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [accountsRes, statusRes] = await Promise.all([
        fetch(`${API_URL}/api/payment-oauth/accounts`, { credentials: "include" }),
        fetch(`${API_URL}/api/payment-oauth/status`, { credentials: "include" })
      ]);
      
      if (accountsRes.ok) {
        const data = await accountsRes.json();
        setLinkedAccounts(data.accounts || []);
      }
      if (statusRes.ok) {
        setOauthStatus(await statusRes.json());
      }
    } catch (err) {
      console.error("Failed to load payment accounts:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
    
    // Check for OAuth callback results
    const params = new URLSearchParams(window.location.search);
    const oauthSuccess = params.get("oauth_success");
    const oauthError = params.get("oauth_error");
    
    if (oauthSuccess) {
      alert(`${oauthSuccess.charAt(0).toUpperCase() + oauthSuccess.slice(1)} account connected successfully!`);
      window.history.replaceState({}, "", window.location.pathname);
    } else if (oauthError) {
      const message = params.get("message") || "Connection failed";
      alert(`Failed to connect ${oauthError}: ${message}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [loadData]);

  async function connectPaymentAccount(provider) {
    setConnectingPayment(provider);
    try {
      const res = await fetch(`${API_URL}/api/payment-oauth/${provider}/authorize`, {
        credentials: "include"
      });
      
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.authorization_url;
      } else {
        const error = await res.json();
        alert(error.detail || "Failed to start connection");
      }
    } catch (err) {
      console.error("Failed to connect payment account:", err);
      alert("Failed to connect payment account");
    }
    setConnectingPayment(null);
  }

  async function unlinkPaymentAccount(provider) {
    if (!window.confirm(`Disconnect your ${provider.charAt(0).toUpperCase() + provider.slice(1)} account?`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/payment-oauth/accounts/${provider}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (res.ok) {
        loadData();
      } else {
        alert("Failed to disconnect account");
      }
    } catch (err) {
      console.error("Failed to unlink account:", err);
    }
  }

  if (loading) {
    return (
      <div className="content payments-page" data-testid="payments-view">
        <div className="section-title">Payments</div>
        <div className="loading-state">Loading payment accounts...</div>
      </div>
    );
  }

  const squareLinked = linkedAccounts.find(a => a.provider === "square");
  const paypalLinked = linkedAccounts.find(a => a.provider === "paypal");
  // AUDIT FIX #4: a provider tile only renders when its integration is REAL
  // (configured server-side) or the fitter already linked it. No "being
  // configured — check back soon" placeholders in a paid product.
  const showSquare = Boolean(oauthStatus.square?.configured || squareLinked);
  const showPaypal = Boolean(oauthStatus.paypal?.configured || paypalLinked);
  const anyProvider = showSquare || showPaypal;

  if (!anyProvider) {
    return (
      <div className="content payments-page" data-testid="payments-view">
        <div className="section-title">Payments</div>
        <div className="empty-state-hg" data-testid="payments-coming-soon">
          <CreditCard size={28} />
          <p>Direct payment collection is coming to CoFitter. Until then, take payment
          the way you do today — nothing about your fittings changes.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content payments-page" data-testid="payments-view">
      <div className="section-title">Payments</div>
      <p className="section-sub">
        Connect your payment accounts to receive payments directly from golfers for fittings and club sales.
      </p>

      {/* Connected Accounts Summary */}
      <div className="payments-summary">
        {showSquare && (
        <div className={`summary-card ${squareLinked ? 'connected' : ''}`}>
          <div className="summary-icon square-icon">
            <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor">
              <path d="M4 4h16v16H4V4zm2 2v12h12V6H6z"/>
            </svg>
          </div>
          <div className="summary-info">
            <span className="summary-label">Square</span>
            <span className={`summary-status ${squareLinked ? 'active' : ''}`}>
              {squareLinked ? 'Connected' : 'Not Connected'}
            </span>
          </div>
          {squareLinked && <CheckCircle2 size={24} className="check-icon" />}
        </div>
        )}
        {showPaypal && (
        <div className={`summary-card ${paypalLinked ? 'connected' : ''}`}>
          <div className="summary-icon paypal-icon">
            <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor">
              <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c1.562 2.008.77 5.112-2.495 6.736-.672.334-1.426.57-2.244.716a13.67 13.67 0 0 1-2.566.22H11.12c-.524 0-.968.382-1.05.9l-1.4 8.89-.396 2.51a.641.641 0 0 0 .633.74h4.012c.457 0 .85-.334.922-.788l.038-.2.73-4.627.047-.256a.93.93 0 0 1 .92-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.755-4.566z"/>
            </svg>
          </div>
          <div className="summary-info">
            <span className="summary-label">PayPal</span>
            <span className={`summary-status ${paypalLinked ? 'active' : ''}`}>
              {paypalLinked ? 'Connected' : 'Not Connected'}
            </span>
          </div>
          {paypalLinked && <CheckCircle2 size={24} className="check-icon" />}
        </div>
        )}
      </div>

      {/* Square Section */}
      {showSquare && (
      <div className="admin-card payment-provider-card">
        <div className="provider-header">
          <div className="provider-logo square-logo">
            <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor">
              <path d="M4 4h16v16H4V4zm2 2v12h12V6H6z"/>
            </svg>
          </div>
          <div className="provider-info">
            <h3>Square</h3>
            <p>Accept in-person and online payments</p>
          </div>
          {squareLinked && (
            <span className="provider-badge connected">
              <CheckCircle2 size={16} /> Connected
            </span>
          )}
        </div>

        {squareLinked ? (
          <div className="provider-connected">
            <div className="connected-details">
              <div className="detail-item">
                <span className="detail-label">Account</span>
                <span className="detail-value">{squareLinked.email || squareLinked.business_name || 'Square Account'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Connected</span>
                <span className="detail-value">{new Date(squareLinked.linked_at).toLocaleDateString()}</span>
              </div>
            </div>
            <button 
              className="btn btn-secondary btn-sm"
              onClick={() => unlinkPaymentAccount("square")}
            >
              Disconnect
            </button>
          </div>
        ) : (
          <div className="provider-connect">
            <ul className="provider-features">
              <li>Accept chip cards with Square Reader</li>
              <li>Contactless & tap-to-pay</li>
              <li>Apple Pay & Google Pay</li>
              <li>Send invoices to golfers</li>
              <li>Real-time transaction alerts</li>
            </ul>
            <button
              className="btn btn-primary"
              onClick={() => connectPaymentAccount("square")}
              disabled={connectingPayment === "square"}
              data-testid="connect-square-btn"
            >
              <Plug size={18} />
              {connectingPayment === "square" ? "Connecting..." : "Connect Square Account"}
            </button>
          </div>
        )}
      </div>
      )}

      {/* PayPal Section */}
      {showPaypal && (
      <div className="admin-card payment-provider-card">
        <div className="provider-header">
          <div className="provider-logo paypal-logo">
            <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor">
              <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c1.562 2.008.77 5.112-2.495 6.736-.672.334-1.426.57-2.244.716a13.67 13.67 0 0 1-2.566.22H11.12c-.524 0-.968.382-1.05.9l-1.4 8.89-.396 2.51a.641.641 0 0 0 .633.74h4.012c.457 0 .85-.334.922-.788l.038-.2.73-4.627.047-.256a.93.93 0 0 1 .92-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.755-4.566z"/>
            </svg>
          </div>
          <div className="provider-info">
            <h3>PayPal</h3>
            <p>Accept online payments worldwide</p>
          </div>
          {paypalLinked && (
            <span className="provider-badge connected">
              <CheckCircle2 size={16} /> Connected
            </span>
          )}
        </div>

        {paypalLinked ? (
          <div className="provider-connected">
            <div className="connected-details">
              <div className="detail-item">
                <span className="detail-label">Account</span>
                <span className="detail-value">{paypalLinked.email || 'PayPal Account'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Connected</span>
                <span className="detail-value">{new Date(paypalLinked.linked_at).toLocaleDateString()}</span>
              </div>
            </div>
            <button 
              className="btn btn-secondary btn-sm"
              onClick={() => unlinkPaymentAccount("paypal")}
            >
              Disconnect
            </button>
          </div>
        ) : (
          <div className="provider-connect">
            <ul className="provider-features">
              <li>Accept PayPal & Venmo</li>
              <li>Credit & debit cards</li>
              <li>Pay Later options for customers</li>
              <li>International payments</li>
              <li>Buyer & seller protection</li>
            </ul>
            <button
              className="btn btn-primary"
              onClick={() => connectPaymentAccount("paypal")}
              disabled={connectingPayment === "paypal"}
              data-testid="connect-paypal-btn"
            >
              <Plug size={18} />
              {connectingPayment === "paypal" ? "Connecting..." : "Connect PayPal Account"}
            </button>
          </div>
        )}
      </div>
      )}

      {/* Help Section */}
      <div className="admin-card help-section">
        <h3>How Payments Work</h3>
        <div className="help-grid">
          <div className="help-item">
            <div className="help-step">1</div>
            <div>
              <strong>Connect your account</strong>
              <p>Link your Square or PayPal business account</p>
            </div>
          </div>
          <div className="help-item">
            <div className="help-step">2</div>
            <div>
              <strong>Accept payments</strong>
              <p>Receive payments for fittings and club sales</p>
            </div>
          </div>
          <div className="help-item">
            <div className="help-step">3</div>
            <div>
              <strong>Get paid directly</strong>
              <p>Funds go straight to your account</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentsView;
